import React, {useEffect} from 'react';

import {useIntl} from 'react-intl';

import {CheckBoxList, colors} from '@stryd/react-ui';

import {Filter, OptionGroup} from '../coaches-page/CoachesFilter';

import {FilterButton} from './FilterButton';
import {FilterContainer} from './FilterContainer';
import {FilterMenu} from './FilterMenu';

type OnFilterChangeProps = {
  checked: boolean;
  value: string;
};

type CheckBoxesFilterProps = {
  filterId: string;
  filter: Filter;
  optionGroups: OptionGroup[];
  onOutsideClick: () => void;
  onMenuButtonClick: () => void;
  onFilterChange: ({checked, value}: OnFilterChangeProps) => void;
  onSaveButtonClick: () => void;
  onResetButtonClick: () => void;
};

const CheckBoxesFilter: React.FC<
  React.PropsWithChildren<CheckBoxesFilterProps>
> = ({
  onOutsideClick,
  onMenuButtonClick,
  onFilterChange,
  onSaveButtonClick,
  onResetButtonClick,
  filterId,
  filter,
  optionGroups,
}) => {
  const intl = useIntl();

  useEffect(() => {
    const closeOnOutsideClick = (event: any) => {
      const clicked = event.target as HTMLButtonElement;

      if (!clicked.closest(`#${filterId}`)) {
        event.stopPropagation();
        onOutsideClick();
      }
    };

    if (filter.menuOpen) {
      window.addEventListener('click', closeOnOutsideClick);
    } else {
      window.removeEventListener('click', closeOnOutsideClick);
    }

    return () => {
      window.removeEventListener('click', closeOnOutsideClick);
    };
  }, [filter.menuOpen, filterId, onOutsideClick]);

  const cbOptionsArray = new Array(optionGroups.length);

  //optionGroups = [{groupId, groupLabel, options},{...}]
  //cbOptionsArray = [[{id, label, checked, onChange}, {...}], [{...}, ...]]

  const groupLabels = optionGroups.map((group) => {
    return intl.formatMessage(group.groupLabelDescriptor);
  });

  optionGroups.forEach((group, index) => {
    cbOptionsArray[index] = group.options.map((option, index) => {
      return {
        id: `${filterId}_group${group.groupId}_${index}`,
        label: intl.formatMessage(option.labelDescriptor),
        checked: filter.value.includes(option.value),
        onChange: (event: any) => {
          onFilterChange({
            checked: event.target.checked,
            value: option.value,
          });
        },
      };
    });
  });

  const legendStyle: React.CSSProperties = {
    color: colors.themes.dark.text,
  };

  const listStyle: React.CSSProperties = {
    display: 'flex',
    flexFlow: 'column nowrap',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    padding: '5px',
  };

  const optionStyle: React.CSSProperties = {
    color: colors.themes.dark.text,
    whiteSpace: 'pre',
    textTransform: 'capitalize',
    marginBottom: '0px',
  };

  return (
    <FilterContainer id={filterId}>
      <FilterButton
        isActive={filter.active}
        menuOpen={filter.menuOpen}
        onClick={onMenuButtonClick}
        filterValue={filter.value}
        label={intl.formatMessage(filter.buttonLabelDescriptor)}
      />
      <FilterMenu
        filterId={filterId}
        menuOpen={filter.menuOpen}
        onResetClick={onResetButtonClick}
        onSaveClick={onSaveButtonClick}
      >
        {cbOptionsArray.map((checkBoxOptions, index) => (
          <CheckBoxList
            key={'cbList_' + groupLabels[index]}
            label={groupLabels[index]}
            options={checkBoxOptions}
            // eslint-disable-next-line
            // @ts-ignore
            legendStyle={legendStyle}
            // eslint-disable-next-line
            // @ts-ignore
            listStyle={listStyle}
            // eslint-disable-next-line
            // @ts-ignore
            optionStyle={optionStyle}
          />
        ))}
      </FilterMenu>
    </FilterContainer>
  );
};

export default CheckBoxesFilter;
